import React from "react";
import {
  Button,
  Space,
  Table,
  TableColumnsType,
  Tag,
  Tooltip,
  Typography,
  Flex,
  Avatar,
} from "antd";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import useOrganization from "../../Hooks/useOrganization";
import { InterviewWithJobPost } from "../../Entities/Interview";
import {
  ClockCircleOutlined,
  UserOutlined,
  MailOutlined,
  CalendarOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import FilterBar from "./FilterBar";
import {
  FindAllInterviewSchedulesFilters,
  PaginationType,
} from "../../Interface";
import "./index.scss";

dayjs.extend(utc);
dayjs.extend(timezone);

// Types
interface InterviewTableProps {
  pagination: PaginationType;
  filters: FindAllInterviewSchedulesFilters;
  filterOptions: {
    jobPosts: { _id: string; title: string }[];
    interviewers: { _id: string; name: string }[];
  };
  interviewSchedules: InterviewWithJobPost[];
  updateFilters: (
    key: keyof FindAllInterviewSchedulesFilters,
    value: string | null
  ) => void;
  handlePageChange: (page: number) => void;
  clearFilters: () => void;
  loading: boolean;
}

// Utility functions
const getStatusColor = (status: "Upcoming" | "Completed"): string => {
  return status === "Upcoming" ? "blue" : "green";
};

const Participants: React.FC<{ participants: any[] }> = ({ participants }) => (
  <Flex vertical gap={4}>
    {participants.map((p: any) => (
      <Flex key={p._id} vertical gap={4}>
        <Space size="small">
          <Avatar size="small" icon={<UserOutlined />} />
          <Flex vertical gap={2}>
            <Typography.Text style={{ fontSize: "12px" }}>
              {p.name}
            </Typography.Text>
          </Flex>
        </Space>
      </Flex>
    ))}
  </Flex>
);

const InterviewParticipants: React.FC<{
  email: string;
  firstName: string;
  lastName: string;
}> = ({ email, firstName, lastName }) => (
  <Flex vertical gap={4}>
    <Space size="small">
      <Avatar size="small" icon={<UserOutlined />} />
      <Flex vertical gap={2}>
        <Typography.Text style={{ fontSize: "12px" }}>
          {firstName} {lastName}
        </Typography.Text>
        <Typography.Link href={`mailto:${email}`} style={{ fontSize: "11px" }}>
          <MailOutlined style={{ fontSize: "10px" }} /> {email}
        </Typography.Link>
      </Flex>
    </Space>
  </Flex>
);

const InterviewActions: React.FC<{
  applicationId: string;
  conferenceId: string;
  startTime: string;
  onViewApplication: (id: string) => void;
  onStartInterview: (id: string) => void;
}> = ({
  applicationId,
  conferenceId,
  startTime,
  onViewApplication,
  onStartInterview,
}) => {
  const isExpired = dayjs(startTime).isAfter(dayjs());
  return (
    <Space direction="vertical" size="small">
      <Button
        size="small"
        type="link"
        onClick={() => onViewApplication(applicationId)}
        style={{ padding: "0", height: "auto", fontSize: "12px" }}
      >
        View Application
      </Button>
      <Tooltip
        title={isExpired ? "Meeting link expired" : "Start Interview"}
        placement="bottom"
      >
        <Button
          size="small"
          type="primary"
          onClick={() => onStartInterview(conferenceId)}
          disabled={new Date(startTime) < new Date()}
          style={{ fontSize: "12px" }}
        >
          Start Interview
        </Button>
      </Tooltip>
    </Space>
  );
};

const ScheduleStatus: React.FC<{ startTime: string }> = ({ startTime }) => {
  const status = dayjs(startTime).isAfter(dayjs()) ? "Upcoming" : "Completed";
  return (
    <Tag
      color={getStatusColor(status)}
      style={{ textTransform: "capitalize", fontSize: "12px" }}
    >
      {status}
    </Tag>
  );
};

// Main Component
const InterviewsTable: React.FC<InterviewTableProps> = ({
  pagination,
  filters,
  filterOptions,
  interviewSchedules,
  updateFilters,
  handlePageChange,
  clearFilters,
  loading,
}) => {
  const { org } = useOrganization();
  const timeZone = org?.timeZone || "UTC";
  const navigate = useNavigate();

  const handleStartInterview = (conferenceId: string) => {
    const path: string = `https://meet.google.com/${conferenceId}`;
    window.location.href = path;
  };

  const handleViewApplication = (applicationId: string, jobPostId: string) => {
    navigate(`/application-details?applicationId=${applicationId}&jobId=${jobPostId}&returnTab=home`);
  };

  const columns: TableColumnsType<any> = [
    {
      title: "Interview Date",
      dataIndex: "startTime",
      key: "startTime",
      width: "20%",
      minWidth: 120,
      render: (_, record) => (
        <Flex vertical gap={4}>
          <Space size="small">
            <CalendarOutlined style={{ fontSize: "12px" }} />
            <Typography.Text style={{ fontSize: "12px" }}>
              {dayjs(record.startTime).tz(timeZone).format("ddd DD MMM YYYY")}
            </Typography.Text>
          </Space>
          <Space size="small">
            <ClockCircleOutlined style={{ fontSize: "12px" }} />
            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
              {dayjs(record.startTime).tz(timeZone).format("h:mm A")} -{" "}
              {dayjs(record.endTime).tz(timeZone).format("h:mm A")}
            </Typography.Text>
          </Space>
        </Flex>
      ),
    },
    {
      title: "Candidate",
      key: "participants",
      width: "25%",
      minWidth: 150,
      render: (_, record) => (
        <InterviewParticipants
          email={record.user.email}
          firstName={record.user.firstName}
          lastName={record.user.lastName}
        />
      ),
    },
    {
      title: "Organizer",
      key: "participants",
      width: "20%",
      minWidth: 200,
      render: (_, record) => (
        <Participants participants={record.participants} />
      ),
    },
    {
      title: "Status",
      key: "status",
      width: "20%",
      minWidth: 100,
      render: (_, record) => <ScheduleStatus startTime={record.startTime} />,
    },
    {
      title: "Job Post",
      key: "jobPost",
      width: "30%",
      minWidth: 100,
      render: (_, record) =>
        record?.jobPost && (
          <Flex vertical gap={4} align="start">
            <Space size="small">
              <DatabaseOutlined style={{ fontSize: "12px" }} />
              <Typography.Text
                style={{
                  fontSize: "12px",
                  display: "inline-block",
                  maxWidth: 180,
                }}
                ellipsis={{
                  tooltip: {
                    title: record?.jobPost?.title,
                    placement: "bottom",
                  },
                }}
              >
                {record?.jobPost?.title}
              </Typography.Text>
            </Space>
            <Space size="small">
              <Button
                type="link"
                size="small"
                style={{ fontSize: "12px" }}
                onClick={() => navigate(`/job-posts/${record?.jobPost?._id}`)}
              >
                View Job Post
              </Button>
            </Space>
          </Flex>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "20%",
      minWidth: 140,
      render: (_, record) => (
        <InterviewActions
          applicationId={record.jobApplication?._id || ""}
          conferenceId={record.conferenceId}
          startTime={record.startTime}
          onViewApplication={()=>handleViewApplication(record.jobApplicationId || "", record.jobPost?._id || "")}
          onStartInterview={handleStartInterview}
        />
      ),
    },
  ];

  const dataSource: any[] = interviewSchedules;

  // Calculate total min-width for horizontal scroll
  const totalMinWidth = columns.reduce((total, column) => {
    return total + (column.minWidth || 100);
  }, 0);

  return (
    <div
      className="interview-table-container"
      style={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}
    >
      <div className="filter-bar-container">
        <FilterBar
          filterOptions={filterOptions}
          filters={filters}
          updateFilters={updateFilters}
          clearFilters={clearFilters}
        />
      </div>

      <div className="table-content-wrapper">
        <Table
          size="small"
          columns={columns}
          dataSource={dataSource}
          rowKey="_id"
          scroll={{ x: totalMinWidth, y: 400 }}
          pagination={{
            current: pagination.page,
            pageSize: pagination.limit,
            onChange: (page) => handlePageChange(page),
            total: pagination.totalRecords,
            showSizeChanger: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
          loading={loading}
          style={{ width: "100%", maxWidth: "100%" }}
        />
      </div>
    </div>
  );
};

export default InterviewsTable;
